import { useMemo } from "react";
import { subDays, startOfDay, endOfDay, startOfMonth, endOfMonth } from "date-fns";
import type { ConnectProto } from "@racoon/api";
import { useGetGovAnalyticsAnomaliesForAllQuery } from "~/shared/api/govDash/govDash.api";

const DAYS_IN_WEEK = 7;
const TOP_PROVIDERS_LIMIT = 5;

export interface UseAnomalyAnalyticsParams {
  districtOrganizationIds: bigint[];
  serviceProviderIds: bigint[];
  selectedDate: Date;
}

export interface AnomalyDayCount {
  date: Date;
  count: number;
}

export interface AnomalyProviderCount {
  organizationId: string;
  organizationName: string;
  count: number;
}

export interface AnomalyAnalytics {
  days: AnomalyDayCount[];
  maxDayCount: number;
  topProviders: AnomalyProviderCount[];
  otherProvidersCount: number;
}

const toFromTo = (date: Date) => ({
  from: { seconds: BigInt(Math.floor(startOfDay(date).getTime() / 1000)) },
  to: { seconds: BigInt(Math.floor(endOfDay(date).getTime() / 1000)) },
});

// anomalyStop is intentionally excluded from the count — same "always show 0"
// business rule already applied on the analytics page (see all-mvos-analytics-overview.tsx).
const anomalyCount = (provider: ConnectProto.GovAnalyticsAnomalyPerProvider): number =>
  Number(provider.dailyDisposalFail ?? 0);

const sumAnomalies = (data: ConnectProto.GovAnalyticsAnomaliesForAll | undefined): number =>
  (data?.anomalyPerProvider ?? []).reduce((sum, provider) => sum + anomalyCount(provider), 0);

/** Real anomaly counts for the Monitor page's "Anomaly analytics" panel: a rolling 7-day
 * daily breakdown ending on `selectedDate`, plus a ranked breakdown by MSW operator (МВО)
 * for the month containing `selectedDate`. */
export const useAnomalyAnalytics = ({
  districtOrganizationIds,
  serviceProviderIds,
  selectedDate,
}: UseAnomalyAnalyticsParams): AnomalyAnalytics => {
  const filter = useMemo(
    () => ({ districtOrganizationIds, serviceProviderIds }),
    [districtOrganizationIds, serviceProviderIds],
  );

  const weekDates = useMemo(
    () => Array.from({ length: DAYS_IN_WEEK }, (_, i) => subDays(selectedDate, DAYS_IN_WEEK - 1 - i)),
    [selectedDate],
  );

  const day0 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[0]) });
  const day1 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[1]) });
  const day2 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[2]) });
  const day3 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[3]) });
  const day4 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[4]) });
  const day5 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[5]) });
  const day6 = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: toFromTo(weekDates[6]) });
  const dayResponses = [day0, day1, day2, day3, day4, day5, day6];

  const monthFromTo = useMemo(() => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    return {
      from: { seconds: BigInt(Math.floor(monthStart.getTime() / 1000)) },
      to: { seconds: BigInt(Math.floor(monthEnd.getTime() / 1000)) },
    };
  }, [selectedDate]);
  const { data: monthAnomalies } = useGetGovAnalyticsAnomaliesForAllQuery({ filter, fromTo: monthFromTo });

  const days: AnomalyDayCount[] = weekDates.map((date, i) => ({
    date,
    count: sumAnomalies(dayResponses[i].data),
  }));
  const maxDayCount = Math.max(1, ...days.map((day) => day.count));

  const providers = (monthAnomalies?.anomalyPerProvider ?? [])
    .map((provider) => ({
      organizationId: String(provider.organizationId ?? ""),
      organizationName: provider.organizationName || "—",
      count: anomalyCount(provider),
    }))
    .filter((provider) => provider.count > 0)
    .sort((a, b) => b.count - a.count);

  const topProviders = providers.slice(0, TOP_PROVIDERS_LIMIT);
  const otherProvidersCount = providers
    .slice(TOP_PROVIDERS_LIMIT)
    .reduce((sum, provider) => sum + provider.count, 0);

  return { days, maxDayCount, topProviders, otherProvidersCount };
};
